import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import {
  CommonModal,
  CommonInput,
  CommonSelect,
  CommonButton,
  CommonStepIndicator,
} from "../../Input";
import Step1CustomerSelection from "../CreateSingleTransaction/Step1CustomerSelection";
import Step1ReceiverList from "../SendBulkTransaction/Step1ReceiverList";
import CreateBeneficiaryModal from "../../Beneficiaries/CreateBeneficiaryModal";
import Swal from "sweetalert2";

const CreateTemplateModal = ({ show, onHide, template }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedReceivers, setSelectedReceivers] = useState([]);
  const [templateDetails, setTemplateDetails] = useState({
    name: "",
    currency: { label: "Singapore Dollar", value: "SGD" },
    sourceOfFunds: { label: "Salary", value: "Salary" },
    receivers: [],
  });
  const [showAddBeneficiary, setShowAddBeneficiary] = useState(false);

  useEffect(() => {
    if (show) {
      if (template) {
        // Mock prefill for edit
        setTemplateDetails({
          name: template.name,
          currency: { label: "Singapore Dollar", value: "SGD" },
          sourceOfFunds: { label: "Salary", value: "Salary" },
          receivers: [
            {
              id: "8385",
              name: "SPM MYR",
              sendAmount: "1",
              receivingAmount: "0.00",
              purpose: { label: "Payroll", value: "Payroll" },
            },
            {
              id: "8384",
              name: "SparkMinds personal",
              sendAmount: "0.00",
              receivingAmount: "1",
              purpose: { label: "Payroll", value: "Payroll" },
            },
          ],
        });
        setSelectedReceivers([
          { id: "8385", name: "SPM MYR" },
          { id: "8384", name: "SparkMinds personal" },
        ]);
        setCurrentStep(3);
      } else {
        resetForm();
      }
    }
  }, [template, show]);

  const resetForm = () => {
    setCurrentStep(1);
    setSelectedCustomer(null);
    setSelectedReceivers([]);
    setTemplateDetails({
      name: "",
      currency: { label: "Singapore Dollar", value: "SGD" },
      sourceOfFunds: { label: "Salary", value: "Salary" },
      receivers: [],
    });
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!selectedCustomer) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Please select a customer first!",
        });
        return;
      }
    }
    if (currentStep === 2) {
      if (selectedReceivers.length === 0) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Please select at least one receiver!",
        });
        return;
      }
      // Initialize receivers in templateDetails if not already done
      const newReceivers = selectedReceivers.map((r) => {
        const existing = templateDetails.receivers.find((tr) => tr.id === r.id);
        return (
          existing || {
            id: r.id,
            name: r.name,
            sendAmount: "",
            receivingAmount: "",
            purpose: { label: "Payroll", value: "Payroll" },
          }
        );
      });
      setTemplateDetails((prev) => ({ ...prev, receivers: newReceivers }));
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => setCurrentStep((prev) => prev - 1);

  const handleCustomerSelect = (customer) => {
    if (customer.kycStatus === "APPROVED") {
      setSelectedCustomer(customer);
      handleNext();
    } else {
      Swal.fire({
        icon: "warning",
        title: "KYC Not Approved",
        text: "This customer cannot be selected because their KYC status is not Approved.",
        confirmButtonColor: "var(--primary)",
      });
    }
  };

  const handleToggleReceiver = (receiver) => {
    if (selectedReceivers.find((r) => r.id === receiver.id)) {
      setSelectedReceivers(
        selectedReceivers.filter((r) => r.id !== receiver.id),
      );
    } else {
      setSelectedReceivers([...selectedReceivers, receiver]);
    }
  };

  const updateReceiverField = (id, field, value) => {
    setTemplateDetails((prev) => ({
      ...prev,
      receivers: prev.receivers.map((r) =>
        r.id === id ? { ...r, [field]: value } : r,
      ),
    }));
  };

  const handleSave = () => {
    Swal.fire({
      icon: "success",
      title: "Success",
      text: template
        ? "Template updated successfully!"
        : "Template created successfully!",
      confirmButtonColor: "var(--primary)",
    }).then(() => {
      onHide();
    });
  };

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      fullscreen={true}
      title={
        <div className="w-100 pe-5">
          <CommonStepIndicator
            steps={[
              "Customer Selection",
              "Beneficiary Selection",
              "Template Input Details",
            ]}
            currentStep={currentStep}
            width={750}
          />
        </div>
      }
      footer={
        <div className="d-flex justify-content-end gap-3 w-100 px-4">
          {currentStep === 2 && (
            <CommonButton
              variant="outline"
              className="px-5 border-light-gray h-45"
              onClick={() => setShowAddBeneficiary(true)}
              style={{ borderRadius: "10px" }}
            >
              Add
            </CommonButton>
          )}
          {currentStep > 1 && (
            <CommonButton
              variant="outline"
              className="px-5 h-45"
              onClick={handleBack}
              style={{ borderRadius: "10px" }}
            >
              Back
            </CommonButton>
          )}
          <CommonButton
            className="px-5 h-45"
            onClick={currentStep === 3 ? handleSave : handleNext}
            style={{ borderRadius: "10px" }}
          >
            {currentStep === 3 ? (template ? "Update" : "Create") : "Next"}
          </CommonButton>
        </div>
      }
    >
      <div className="px-4 py-2" style={{ minHeight: "500px" }}>
        {currentStep === 1 && (
          <Step1CustomerSelection
            selectedCustomer={selectedCustomer}
            onSelectCustomer={handleCustomerSelect}
          />
        )}
        {currentStep === 2 && (
          <Step1ReceiverList
            selectedReceivers={selectedReceivers}
            onToggleReceiver={handleToggleReceiver}
          />
        )}
        {currentStep === 3 && (
          <div>
            <Row className="g-3 mb-4">
              <Col md={4}>
                <label className="text-muted fs-12 mb-1 text-nowrap">Template Name</label>
                <CommonInput
                  value={templateDetails.name}
                  onChange={(e) =>
                    setTemplateDetails({
                      ...templateDetails,
                      name: e.target.value,
                    })
                  }
                  placeholder="Enter template name"
                  style={{ backgroundColor: "#EFF6FF" }}
                />
              </Col>
              <Col md={4}>
                <label className="text-muted fs-12 mb-1 text-nowrap">Send Currency</label>
                <CommonSelect
                  value={templateDetails.currency}
                  onChange={(val) =>
                    setTemplateDetails({ ...templateDetails, currency: val })
                  }
                  options={[{ label: "Singapore Dollar", value: "SGD" }]}
                />
              </Col>
              <Col md={4}>
                <label className="text-muted fs-12 mb-1 text-nowrap">Source of Funds</label>
                <CommonSelect
                  value={templateDetails.sourceOfFunds}
                  onChange={(val) =>
                    setTemplateDetails({
                      ...templateDetails,
                      sourceOfFunds: val,
                    })
                  }
                  options={[{ label: "Salary", value: "Salary" }]}
                />
              </Col>
            </Row>

            {templateDetails.receivers.map((receiver, index) => (
              <div
                key={receiver.id}
                className="card border p-3 mb-3 bg-light-gray-soft"
                style={{ borderRadius: "12px" }}
              >
                <h6 className="fw-bold text-primary mb-3">
                  #{index + 1} {receiver.name}
                </h6>
                <Row className="g-3">
                  <Col md={4}>
                    <label className="text-muted fs-12 mb-1 text-nowrap">Send Amount</label>
                    <CommonInput
                      value={receiver.sendAmount}
                      onChange={(e) =>
                        updateReceiverField(
                          receiver.id,
                          "sendAmount",
                          e.target.value,
                        )
                      }
                      placeholder="0.00"
                    />
                  </Col>
                  <Col md={4}>
                    <label className="text-muted fs-12 mb-1 text-nowrap">
                      Receiving Amount
                    </label>
                    <CommonInput
                      value={receiver.receivingAmount}
                      onChange={(e) =>
                        updateReceiverField(
                          receiver.id,
                          "receivingAmount",
                          e.target.value,
                        )
                      }
                      placeholder="0.00"
                    />
                  </Col>
                  <Col md={4}>
                    <label className="text-muted fs-12 mb-1 text-nowrap">
                      Purpose of Transfer
                    </label>
                    <CommonSelect
                      value={receiver.purpose}
                      onChange={(val) =>
                        updateReceiverField(receiver.id, "purpose", val)
                      }
                      options={[{ label: "Payroll", value: "Payroll" }]}
                    />
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateBeneficiaryModal
        show={showAddBeneficiary}
        onHide={() => setShowAddBeneficiary(false)}
      />
    </CommonModal>
  );
};

export default CreateTemplateModal;
